// Mobile port of the web Projects Configuration editor (Settings → Projects
// Configuration → Form Prompts / Component Labels). Reads/writes
// user_settings.project_form_prompts and user_settings.project_component_labels
// directly — same shape as web's ProjectFormPrompt / ProjectComponentLabel
// (src/interfaces/settings.ts).
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GoldButton, GlassCard } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

interface ProjectFormPrompt {
  id: string;
  text: string;
}

interface ProjectComponentLabel {
  id: string;
  name: string;
  colorClass: string;
  fontSize?: string;
  fontColorClass?: string;
}

const FONT_SIZES = ['default', 'text-xs', 'text-sm', 'text-base', 'text-lg', 'text-xl'];

export default function ProjectsEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value: prompts, setValue: setPrompts, loading: promptsLoading } = useSettingColumn<ProjectFormPrompt[]>(
    userId,
    'project_form_prompts',
    []
  );
  const { value: labels, setValue: setLabels, loading: labelsLoading } = useSettingColumn<ProjectComponentLabel[]>(
    userId,
    'project_component_labels',
    []
  );

  const movePrompt = (index: number, direction: -1 | 1) => {
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= prompts.length) return;
    const next = [...prompts];
    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    setPrompts(next);
  };

  const updatePrompt = (id: string, text: string) => {
    setPrompts(prompts.map((p) => (p.id === id ? { ...p, text } : p)));
  };

  const deletePrompt = (id: string) => {
    setPrompts(prompts.filter((p) => p.id !== id));
  };

  const addPrompt = () => {
    setPrompts([...prompts, { id: `prompt-${Date.now()}`, text: 'New Prompt' }]);
  };

  const updateLabel = (id: string, patch: Partial<ProjectComponentLabel>) => {
    setLabels(labels.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const deleteLabel = (id: string) => {
    setLabels(labels.filter((l) => l.id !== id));
  };

  const addLabel = () => {
    setLabels([...labels, { id: `label-${Date.now()}`, name: 'New Label', colorClass: 'bg-gray-200' }]);
  };

  const cycleFontSize = (label: ProjectComponentLabel) => {
    const current = label.fontSize ?? 'default';
    const idx = FONT_SIZES.indexOf(current);
    const nextIdx = (idx + 1) % FONT_SIZES.length;
    const next = FONT_SIZES[nextIdx];
    updateLabel(label.id, { fontSize: next === 'default' ? undefined : next });
  };

  if (promptsLoading || labelsLoading) {
    return (
      <View style={styles.container}>
        <Text style={styles.hint}>Loading…</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SectionTitle style={styles.sectionGap}>Form Prompts</SectionTitle>
      <Text style={styles.hint}>Prompts shown when creating a new project.</Text>
      {prompts.length === 0 ? <Text style={styles.hint}>No prompts yet.</Text> : null}

      {prompts.map((prompt, idx) => (
        <View key={prompt.id} style={styles.row}>
          <View style={styles.reorder}>
            <Pressable
              onPress={() => movePrompt(idx, -1)}
              disabled={idx === 0}
              style={[styles.reorderBtn, idx === 0 && styles.reorderBtnDisabled]}
            >
              <Text style={styles.reorderText}>▲</Text>
            </Pressable>
            <Pressable
              onPress={() => movePrompt(idx, 1)}
              disabled={idx === prompts.length - 1}
              style={[styles.reorderBtn, idx === prompts.length - 1 && styles.reorderBtnDisabled]}
            >
              <Text style={styles.reorderText}>▼</Text>
            </Pressable>
          </View>
          <Field style={styles.flex1}
            inputStyle={[styles.input, styles.flex1]}
            value={prompt.text}
            onChangeText={(t) => updatePrompt(prompt.id, t)}
            placeholder="Prompt text"
          />
          <Pressable style={styles.deleteBtn} onPress={() => deletePrompt(prompt.id)}>
            <Text style={styles.deleteText}>✕</Text>
          </Pressable>
        </View>
      ))}

      <GoldButton label="+ Add Prompt" onPress={addPrompt} style={styles.actionGap} />

      <SectionTitle style={styles.sectionGap}>Component Labels</SectionTitle>
      <Text style={styles.hint}>Labels available for project components. Color as hex (#93c5fd) or Tailwind class.</Text>
      {labels.length === 0 ? <Text style={styles.hint}>No labels yet.</Text> : null}

      {labels.map((label) => (
        <GlassCard key={label.id} style={styles.card}>
          <View style={styles.row}>
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              value={label.name}
              onChangeText={(t) => updateLabel(label.id, { name: t })}
              placeholder="Label name"
            />
            <Pressable style={styles.deleteBtn} onPress={() => deleteLabel(label.id)}>
              <Text style={styles.deleteText}>✕</Text>
            </Pressable>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Color</Text>
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              value={label.colorClass}
              onChangeText={(t) => updateLabel(label.id, { colorClass: t })}
              placeholder="#93c5fd or bg-blue-200"
              autoCapitalize="none"
            />
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Font size</Text>
            <Pressable style={styles.pickerBtn} onPress={() => cycleFontSize(label)}>
              <Text style={styles.pickerText}>{label.fontSize ?? 'default'}</Text>
            </Pressable>
          </View>
        </GlassCard>
      ))}

      <GoldButton label="+ Add Label" onPress={addLabel} style={styles.actionGap} />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  container: { gap: 8 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  card: {
    padding: 12,
    marginTop: 10,
    gap: 8,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 },
  flex1: { flex: 1 },
  reorder: { gap: 2 },
  reorderBtn: {
    width: 26,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  reorderBtnDisabled: { opacity: 0.25 },
  reorderText: { fontSize: 10, color: t.goldBright, fontWeight: '900' },
  label: { fontSize: 12, color: t.ivorySoft, fontWeight: '700' },
  input: {
  },
  pickerBtn: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  pickerText: { fontSize: 13, fontWeight: '700', color: t.goldBright },
  deleteBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  deleteText: { color: t.lapsed, fontWeight: '900' },
}),
);
